<template>
  <div v-if="visible" class="risk-reminder-banner" :class="{ 'risk-reminder-banner--stacked': noBottomMargin }">
    <div class="risk-reminder-banner__inner">
      <div class="risk-reminder-banner__body">
        <div class="risk-reminder-banner__icon" aria-hidden="true">!</div>
        <div class="risk-reminder-banner__content">
          <p class="risk-reminder-banner__text">
            <span class="risk-reminder-banner__headline">{{ i18n('global.risk_banner.headline') }}</span>
            <span>{{ i18n('global.risk_banner.description_before_not') }}</span>
            <span class="risk-reminder-banner__emphasis">{{ i18n('global.risk_banner.description_not') }}</span>
            <span>{{ i18n('global.risk_banner.description_after_not') }}</span>
          </p>
        </div>
      </div>
      <div class="risk-reminder-banner__actions">
        <button class="risk-reminder-banner__contact" type="button" @click="onContactClick">
          {{ i18n('global.risk_banner.contact_us') }}
        </button>
        <button
          class="risk-reminder-banner__close"
          type="button"
          :aria-label="i18n('global.risk_banner.close')"
          @click="onCloseClick"
        >
          ×
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from '../../store';

const DISMISS_KEY = 'RISK_REMINDER_BANNER_DISMISSED';

defineProps<{
  noBottomMargin?: boolean;
}>();

const router = useRouter();
const store = useStore();
const { t: i18n } = useI18n();

const visible = ref(true);

onMounted(() => {
  if (typeof window === 'undefined') return;
  visible.value = window.localStorage.getItem(DISMISS_KEY) !== '1';
});

const onContactClick = () => {
  const accessToken = store.getters.accessToken;
  router.push({ path: accessToken ? '/dashboard/support' : '/contact_us' });
};

const onCloseClick = () => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(DISMISS_KEY, '1');
  }
  visible.value = false;
};
</script>

<style lang="less" scoped>
.risk-reminder-banner {
  width: 100%;
  margin-bottom: 24px;
  padding: 0 16px;
  box-sizing: border-box;
  background:
    linear-gradient(90deg, rgba(255, 205, 53, 0.08) 0%, rgba(255, 205, 53, 0.08) 100%),
    linear-gradient(90deg, #fff 0%, #fff 100%);

  &--stacked {
    margin-bottom: 0;
  }

  &--stacked &__inner {
    border-radius: 8px 8px 0 0;
  }

  &__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 72px;
    padding: 0 16px;
    border-radius: 8px;
  }

  &__body {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex: 1 1 auto;
    min-width: 0;
    padding: 16px 52px 16px 0;
  }

  &__icon {
    width: 18px;
    height: 18px;
    flex: 0 0 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
    border-radius: 50%;
    background: #ffcd35;
    color: #7f5c0e;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
  }

  &__content {
    min-width: 0;
    flex: 1 1 auto;
  }

  &__text {
    margin: 0;
    color: #a67812;
    font-family: var(--font-secondary);
    font-size: 12px;
    font-weight: 400;
    line-height: 19.5px;
    letter-spacing: 0.25px;
  }

  &__headline {
    margin-right: 4px;
    color: #7f5c0e;
    font-weight: 700;
  }

  &__emphasis {
    color: #7f5c0e;
    font-weight: 700;
  }

  &__actions {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
  }

  &__contact {
    min-height: 48px;
    padding: 8px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #7f5c0e;
    font-family: var(--font-secondary);
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 0.4px;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;

    &:hover,
    &:focus,
    &:active {
      background: transparent;
      color: var(--Primary-Variant, #FFB81C);
    }
  }

  &__close {
    width: 48px;
    height: 48px;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: rgba(166, 120, 18, 0.45);
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;

    &:hover,
    &:focus,
    &:active {
      background: transparent;
      color: #1e1b16;
    }
  }
}

@media screen and (max-width: 640px) {
  .risk-reminder-banner {
    margin-bottom: 16px;
    padding: 0 16px;

    &--stacked {
      margin-bottom: 0;
    }

    &__inner {
      align-items: flex-start;
      gap: 0;
      min-height: 0;
      padding: 16px;
    }

    &__body {
      gap: 8px;
      padding: 16px 16px 16px 0;
    }

    &__actions {
      align-items: flex-start;
    }

    &__contact {
      display: none;
    }

    &__close {
      width: 24px;
      height: 24px;
      margin-top: 4px;
      border-radius: 999px;
      font-size: 20px;
    }

    &__text {
      line-height: 19.5px;
    }
  }
}

</style>
